import React from 'react';

import QuotationCommonWrapper from './Common/QuotationCommonWrapper';
import QuotationHeaderBar from './Common/QuotationHeaderBar';
import QuotationList from './QuotationList';
import NotFoundQuotation from './NotFoundQuotation';
import QuotationLoading from './Common/QuotationLoading';

import {
  ReceivedQuotationNormalForm,
  ReceivedQuotationDesignForm
} from '@components/Form';

import {
  CapaQuotationServiceType,
  CapaQuotationUserRfqDetail,
  CapaQuotationDetailedQuotation,
  CapaQuotationPartnerStrategy,
  CapaPcbServiceType,
  CapaQuotationChatRoom,
  CapaQuotationChatRoomStatus
} from '@generated/graphql';

interface Props {
  matches: boolean;
  isLoading: boolean;
  isFirstCreatedQuotation: boolean;
  isLatestQuotation: boolean;
  partner?: Omit<CapaQuotationPartnerStrategy, 'email'>;
  quotation?: CapaQuotationDetailedQuotation;
  rfqDetail?: CapaQuotationUserRfqDetail;
  chatRoom?: CapaQuotationChatRoom;
}

const ReceivedQuotationFormPresenter = ({
  quotation,
  partner,
  rfqDetail,
  chatRoom,
  matches,
  isLoading,
  isFirstCreatedQuotation,
  isLatestQuotation
}: Props) => {
  const haveConsulted = !!quotation;

  const designServiceType =
    CapaQuotationServiceType.Design ===
      (quotation?.serviceType as CapaQuotationServiceType) ||
    (rfqDetail?.serviceType === CapaQuotationServiceType.Pcb &&
      rfqDetail?.pcb?.services?.some(
        (service) => service?.serviceType === CapaPcbServiceType.CircuitDesign
      ));

  const isWithdraw = partner?.status === 'DELETED';
  const isClosedConsulting = chatRoom?.chatRoomStatus
    ? [
        CapaQuotationChatRoomStatus.ClosedBeforeConsulting,
        CapaQuotationChatRoomStatus.ClosedClient,
        CapaQuotationChatRoomStatus.ClosedConfirm,
        CapaQuotationChatRoomStatus.ClosedPartner
      ].includes(chatRoom?.chatRoomStatus)
    : false;

  if (isLoading) {
    return (
      <>
        <QuotationHeaderBar hasQuotation={Boolean(quotation)} />
        <QuotationLoading />
      </>
    );
  }

  if (!quotation) {
    return (
      <NotFoundQuotation
        rfqId={rfqDetail?.id}
        partnerId={partner?.id}
        chatRoomStatus={chatRoom?.chatRoomStatus ?? undefined}
      />
    );
  }

  return (
    <>
      <QuotationList
        matches={matches}
        rfqId={rfqDetail?.id}
        partnerId={partner?.id}
      />

      <QuotationHeaderBar hasQuotation={Boolean(quotation)} />

      <QuotationCommonWrapper
        isLatestQuotation={isLatestQuotation}
        isFirstCreatedQuotation={isFirstCreatedQuotation}
        isWithdraw={isWithdraw}
        isClosedConsulting={isClosedConsulting}
        createdAt={quotation?.createdAt ?? undefined}
        rfqId={rfqDetail?.id}
        partnerId={partner?.id}
        quotationId={quotation?.id}
        projectName={rfqDetail?.projectName ?? undefined}
      >
        {designServiceType ? (
          <ReceivedQuotationDesignForm
            {...quotation}
            matches={matches}
            haveConsulted={haveConsulted}
            rfqId={rfqDetail?.id}
          />
        ) : (
          <ReceivedQuotationNormalForm
            {...quotation}
            matches={matches}
            haveConsulted={haveConsulted}
            rfqId={rfqDetail?.id}
          />
        )}
      </QuotationCommonWrapper>
    </>
  );
};

export default ReceivedQuotationFormPresenter;
